/**
 * Wire types for the agency white-label client portal.
 *
 * Client-safe: no server imports, no `@repo/database`. Everything here is
 * re-exported from `@repo/core/client`, so keep it to plain types.
 *
 * Dates are serialised to ISO strings at the service boundary rather than left
 * as `Date`, because every consumer of these DTOs is either an API response or
 * a React server->client prop.
 */

/** Mirrors the Prisma `PortalDomainStatus` enum without importing it. */
export type PortalDomainStatusValue = "PENDING" | "ACTIVE" | "ERROR";

/** One DNS record the agency has to create for the portal hostname to work. */
export interface PortalDnsRecord {
	type: "CNAME" | "TXT";
	name: string;
	value: string;
	/** Provider-supplied explanation, e.g. why a TXT record is required. */
	reason?: string;
	/**
	 * The registrable zone `name` is relative to (e.g. "spyglasses.ai"). Present
	 * on every record we write; absent only on rows stored before it existed.
	 */
	zone?: string;
}

export interface PortalDomainDto {
	id: string;
	organizationId: string;
	hostname: string;
	status: PortalDomainStatusValue;
	verificationRecords: PortalDnsRecord[];
	lastCheckedAt: string | null;
	verifiedAt: string | null;
	lastError: string | null;
	createdAt: string;
	updatedAt: string;
}

/**
 * A property's portal, as shown in the agency's settings UI.
 *
 * NEVER carries the password. Revealing it is a separate, deliberately
 * explicit call (`portalSiteService.revealPassword`) so a plaintext credential
 * can never ride along in a list response.
 */
export interface PortalSiteDto {
	id: string;
	propertyId: string;
	portalDomainId: string;
	slug: string;
	enabled: boolean;
	passwordVersion: number;
	passwordUpdatedAt: string;
	lockedUntil: string | null;
	lastUnlockedAt: string | null;
	url: string;
	createdAt: string;
	updatedAt: string;
}

/** White-label chrome shown to the portal visitor. */
export interface PortalBranding {
	agencyName: string;
	agencyLogoUrl: string | null;
}

/**
 * One unlocked portal site inside a visitor's session cookie.
 *
 * `pv` pins the grant to the password version it was issued against, so
 * rotating a password invalidates outstanding sessions without any server-side
 * session store.
 */
export type PortalGrant = { exp: number; pv: number };

/** Keyed by `PortalSite.id`. */
export type PortalGrants = Record<string, PortalGrant>;
